// umlaut-check: skip — "zuruecksetzen" ist der technische Name aus
// useGemerkteFilter.jsx, bleibt ASCII.
import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {router, useForm, usePage} from "@inertiajs/react";
import {Badge, Button, Card, Container, Form, Modal, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {ReactSelectBootstrap} from "react-select-bootstrap";
import {allExpanded, defaultStyles, JsonView} from "react-json-view-lite";
import {PiFunnel, PiFunnelFill} from "react-icons/pi";
import KeyValueTable from "../Components/KeyValueTable.jsx";
import HelpTooltip from "../Components/HelpTooltip.jsx";
import PermissionMatrix, {PermissionSaveBar, usePermissionDraft} from "../Components/PermissionMatrix.jsx";
import ConfirmDialog from "../Components/OrderEditor/ConfirmDialog.jsx";
import UserFormFields from "../Components/Users/UserFormFields.jsx";
import useGemerkteFilter from "../Components/Filter/useGemerkteFilter.jsx";

// T12: Suche, Spaltenfilter, Statusauswahl und Sortierung bleiben je
// Benutzer beim Verlassen/Zurückkommen erhalten (useGemerkteFilter, Paket
// S5) - "sortKey"/"sortDir" sind bewusst Teil desselben gemerkten
// Zustands wie die übrigen Filter, kein zweiter Speicher.
const STANDARD_FILTER = {
    query: "",
    name: "",
    email: "",
    weclapp_id: "",
    department_id: "",
    role_id: "",
    status: "active",
    sortKey: "name",
    sortDir: "asc",
};

const normalise = value => String(value ?? "").trim().toLocaleLowerCase("de");
const roleLabel = name => (name === "superadmin" ? "Superadmin" : name);

/**
 * Übernimmt die Ansicht eines Mitarbeiters ("Als dieser Benutzer anmelden").
 *
 * Bewusst im selben Tab: Eine Anmeldung gilt ohnehin für den ganzen Browser,
 * nicht für einen einzelnen Tab. Ein neuer Tab hätte also nur vorgetäuscht,
 * dass daneben noch das eigene Konto weiterläuft. Der Weg zurück steht
 * durchgehend im Balken am unteren Rand.
 */
function SuperadminBadge() {
    return (
        <span className="d-inline-flex align-items-center gap-1 users-superadmin-badge">
            <Badge pill bg="dark">Superadmin</Badge>
            <HelpTooltip id="superadmin-badge-hilfe" ariaLabel="Hilfe zum Superadmin-Konto" placement="top">
                Enthält intern auch die Rolle Admin, die für ältere Prüfungen auf „role:admin“ erzwungen
                wird. Das Konto lässt sich hier nur ansehen.
            </HelpTooltip>
        </span>
    );
}

function alsBenutzerAnmelden(user) {
    router.post(`/users/${user.id}/impersonate`);
}

export default function Users({
    users = [],
    roles = [],
    departments = [],
    printers = [],
    printersAvailable = true,
    can_manage: canManage = false,
    permission_catalog: permissionCatalog = null,
    permission_areas: permissionAreas = [],
}) {
    const benutzerId = usePage().props.auth?.id;
    const {
        werte: filter,
        setWert: setFilterWert,
        setWerte: setFilterWerte,
        zuruecksetzen: resetFilters,
        weichtAb: hasChangedFilters,
    } = useGemerkteFilter("users", STANDARD_FILTER, {benutzerId});
    const [selected, setSelected] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [inlineError, setInlineError] = useState("");
    const [permissionsSaving, setPermissionsSaving] = useState(false);
    const [permissionsError, setPermissionsError] = useState("");
    const [confirmClose, setConfirmClose] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [showCopySettingsModal, setShowCopySettingsModal] = useState(false);
    const [selectedUserForCopy, setSelectedUserForCopy] = useState(null);
    // Nur auf schmalen Fenstern sichtbar (Kartendarstellung, siehe app.scss
    // ".users-table") - klappt die dort eingeklappte Kopf-/Filterzeile auf,
    // damit Sortierung und Spaltenfilter auch mobil erreichbar bleiben.
    const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
    const editForm = useForm({
        name: "",
        email: "",
        weclapp_id: "",
        role_id: [],
        department_ids: [],
        password: "",
        color: "#000000",
        image: null,
        homepage_route: "",
        is_active: true,
        delete_image: false,
        default_printer: "",
    });

    // Einzelrechte des geöffneten Benutzers (nur Superadmin, sonst ist der
    // Katalog null): gespeicherter Stand = direkt vergebene Rechte, der
    // Entwurf kommt aus der gemeinsamen Matrix.
    const permissionDraft = usePermissionDraft(selected?.direct_permissions || []);

    const sortableValue = (user, key) => {
        if (key === "roles") return (user.roles || []).map(role => role.name).join(", ");
        if (key === "departments") return (user.departments || []).map(department => department.name).join(", ");
        if (key === "is_active") return user.is_active ? "1" : "0";
        return user[key] || "";
    };

    const searchableValue = user => [
        user.name,
        user.email,
        user.weclapp_id,
        ...(user.roles || []).map(role => roleLabel(role.name)),
        ...(user.departments || []).map(department => department.name),
        user.is_active ? "aktiv" : "inaktiv deaktiviert",
    ].join(" ");

    const visibleUsers = useMemo(() => users
        .filter(user => filter.status === "all" || (filter.status === "active") === !!user.is_active)
        .filter(user => normalise(user.name).includes(normalise(filter.name)))
        .filter(user => normalise(user.email).includes(normalise(filter.email)))
        .filter(user => normalise(user.weclapp_id).includes(normalise(filter.weclapp_id)))
        .filter(user => !filter.department_id || (user.departments || []).some(department => String(department.id) === filter.department_id))
        .filter(user => !filter.role_id || (user.roles || []).some(role => String(role.id) === filter.role_id))
        .filter(user => normalise(searchableValue(user)).includes(normalise(filter.query)))
        .sort((a, b) => String(sortableValue(a, filter.sortKey)).localeCompare(
            String(sortableValue(b, filter.sortKey)),
            "de",
            {numeric: true, sensitivity: "base"},
        ) * (filter.sortDir === "asc" ? 1 : -1)), [users, filter]);

    const updateColumnFilter = (key, value) => setFilterWert(key, value);
    const sortBy = key => {
        if (filter.sortKey === key) {
            setFilterWert("sortDir", filter.sortDir === "asc" ? "desc" : "asc");
        } else {
            setFilterWerte({sortKey: key, sortDir: "asc"});
        }
    };

    const openEdit = user => {
        editForm.clearErrors();
        setImagePreview(null);
        setSelectedUserForCopy(null);
        editForm.setData({
            name: user.name,
            email: user.email,
            weclapp_id: user.weclapp_id || "",
            role_id: (user.roles || []).map(role => role.id),
            department_ids: (user.departments || []).map(department => department.id),
            password: "",
            color: user.color || "#000000",
            image: user.preview_image || user.image || null,
            homepage_route: user.homepage_route || "",
            is_active: !!user.is_active,
            delete_image: false,
            default_printer: user.settings_data?.["ordereditor.printer_fixed"] || "",
        });
        setSelected(user);
    };

    // Direktlink „/user?bearbeiten=<id>“ öffnet den Dialog dieses Kontos,
    // z. B. für die geschützten Rechte des Meetingraums (Auftrag #1588).
    useEffect(() => {
        const id = Number(new URLSearchParams(window.location.search).get("bearbeiten"));
        const treffer = id ? users.find(u => u.id === id) : null;
        if (treffer) {
            openEdit(treffer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeEdit = (force = false) => {
        if (editForm.processing) return;
        if (force !== true && permissionDraft.count > 0) {
            setConfirmClose(true);
            return;
        }
        setConfirmClose(false);
        setPermissionsError("");
        setSelected(null);
        setImagePreview(null);
        editForm.clearErrors();
    };

    const submitEdit = async event => {
        event.preventDefault();
        if (!selected) return;
        // Offene Rechte-Änderungen gehen mit, damit sie beim Speichern des
        // Benutzers nicht stillschweigend verloren gehen.
        if (permissionCatalog && permissionDraft.count > 0 && !(await savePermissions())) return;
        editForm.post(`/user/${selected.id}`, {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                setSelected(null);
                setImagePreview(null);
            },
        });
    };

    const sortHeader = (key, label) => (
        <button
            type="button"
            className="btn btn-link p-0 text-decoration-none fw-semibold"
            onClick={() => sortBy(key)}
        >
            {label}
            {filter.sortKey === key && <span className="ms-1" aria-label={filter.sortDir === "asc" ? "aufsteigend" : "absteigend"}>{filter.sortDir === "asc" ? "▲" : "▼"}</span>}
        </button>
    );

    const saveInline = async (user, field, ids) => {
        if (!canManage || !user.can_manage) return;
        setSavingId(`${user.id}:${field}`);
        setInlineError("");
        try {
            await axios.patch(
                `/users/${user.id}/${field === "roles" ? "roles-inline" : "departments-inline"}`,
                field === "roles" ? {role_id: ids} : {department_ids: ids},
            );
            router.reload({
                only: ["users"],
                preserveScroll: true,
                onFinish: () => setSavingId(null),
            });
        } catch (error) {
            setInlineError(error.response?.data?.message || "Änderung konnte nicht gespeichert werden.");
            setSavingId(null);
        }
    };

    // Nur für den Superadmin (permissionCatalog ist für alle anderen null,
    // siehe UserController::index). Ein Request je Speichern mit dem Diff
    // {grant, revoke}; betrifft nur direkt vergebene Rechte, nie eine Rolle.
    const savePermissions = async () => {
        if (!selected || permissionDraft.count === 0) return true;
        setPermissionsSaving(true);
        setPermissionsError("");
        try {
            const {data} = await axios.put(`/users/${selected.id}/permissions`, {
                grant: permissionDraft.grant,
                revoke: permissionDraft.revoke,
            });
            const protectedNames = new Set((permissionCatalog || []).filter(item => item.protected).map(item => item.name));
            setSelected(current => (current && current.id === selected.id ? {
                ...current,
                direct_permissions: data.direct_permissions,
                protected_permissions_granted: data.direct_permissions.filter(name => protectedNames.has(name)),
            } : current));
            router.reload({only: ["users"], preserveScroll: true});
            return true;
        } catch (error) {
            setPermissionsError(error.response?.data?.message || "Änderung konnte nicht gespeichert werden.");
            return false;
        } finally {
            setPermissionsSaving(false);
        }
    };

    const copySettings = () => {
        if (!selected || !selectedUserForCopy) return;
        router.get(`/user/copysettings/${selectedUserForCopy.value}/${selected.id}`);
        setShowCopySettingsModal(false);
    };

    const copyUserOptions = users
        .filter(user => user.id !== selected?.id)
        .map(user => ({value: user.id, label: user.name}));
    const jsonStyle = {...defaultStyles, container: "json-view-container"};
    const menuPortalTarget = typeof document === "undefined" ? undefined : document.body;

    return (
        <Container fluid className="py-3">
            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Benutzer</h1>
                    <small className="text-muted">Rollen, Abteilungen und Weclapp-Zuordnung auf einen Blick</small>
                </div>
                {canManage && <Button onClick={() => router.get("/user/create")}>Benutzer anlegen</Button>}
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                <Form.Control
                    aria-label="Alle Benutzerspalten durchsuchen"
                    placeholder="Alle Spalten durchsuchen…"
                    value={filter.query}
                    onChange={event => setFilterWert("query", event.target.value)}
                    style={{maxWidth: 360}}
                />
                <div className="d-flex gap-2 align-items-center small text-muted">
                    <span>{visibleUsers.length} von {users.length} Benutzern</span>
                    {hasChangedFilters && <button type="button" className="btn btn-link btn-sm p-0" onClick={resetFilters}>Filter und Sortierung zurücksetzen</button>}
                </div>
            </div>

            {inlineError && <div className="alert alert-danger" role="alert">{inlineError}</div>}

            {/* Nur auf schmalen Fenstern sichtbar (Kartendarstellung) - klappt
                Sortierung und Spaltenfilter auf, die dort sonst eingeklappt
                sind (analog zur Werkzeugleiste des DataGrid). */}
            <Button
                variant={mobileToolsOpen ? "secondary" : "outline-secondary"}
                size="sm"
                className="d-lg-none d-inline-flex align-items-center gap-1 mb-2"
                onClick={() => setMobileToolsOpen(open => !open)}
                aria-expanded={mobileToolsOpen}
            >
                {hasChangedFilters ? <PiFunnelFill className="me-1"/> : <PiFunnel className="me-1"/>}
                Sortierung &amp; Filter
            </Button>

            <Table hover responsive className={`align-middle bg-white users-table${mobileToolsOpen ? " users-table-tools-open" : ""}`}>
                <thead>
                    <tr>
                        <th aria-sort={filter.sortKey === "name" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("name", "Name")}</th>
                        <th aria-sort={filter.sortKey === "email" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("email", "E-Mail")}</th>
                        <th aria-sort={filter.sortKey === "weclapp_id" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("weclapp_id", "Weclapp-ID")}</th>
                        <th aria-sort={filter.sortKey === "departments" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("departments", "Abteilungen")}</th>
                        <th aria-sort={filter.sortKey === "roles" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("roles", "Rollen")}</th>
                        <th aria-sort={filter.sortKey === "is_active" ? (filter.sortDir === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("is_active", "Status")}</th>
                        <th aria-label="Aktionen"/>
                    </tr>
                    <tr className="table-light align-top">
                        <th data-col-label="Name filtern"><Form.Control size="sm" aria-label="Name filtern" value={filter.name} onChange={event => updateColumnFilter("name", event.target.value)} placeholder="Filtern…"/></th>
                        <th data-col-label="E-Mail filtern"><Form.Control size="sm" aria-label="E-Mail filtern" value={filter.email} onChange={event => updateColumnFilter("email", event.target.value)} placeholder="Filtern…"/></th>
                        <th data-col-label="Weclapp-ID filtern"><Form.Control size="sm" aria-label="Weclapp-ID filtern" value={filter.weclapp_id} onChange={event => updateColumnFilter("weclapp_id", event.target.value)} placeholder="Filtern…"/></th>
                        <th data-col-label="Abteilung filtern">
                            <Form.Select size="sm" aria-label="Abteilung filtern" value={filter.department_id} onChange={event => updateColumnFilter("department_id", event.target.value)}>
                                <option value="">Alle</option>
                                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                            </Form.Select>
                        </th>
                        <th data-col-label="Rolle filtern">
                            <Form.Select size="sm" aria-label="Rolle filtern" value={filter.role_id} onChange={event => updateColumnFilter("role_id", event.target.value)}>
                                <option value="">Alle</option>
                                {roles.map(role => <option key={role.id} value={role.id}>{roleLabel(role.name)}</option>)}
                            </Form.Select>
                        </th>
                        <th data-col-label="Status filtern">
                            <Form.Select size="sm" aria-label="Status filtern" value={filter.status} onChange={event => setFilterWert("status", event.target.value)}>
                                <option value="active">Nur aktive</option>
                                <option value="inactive">Nur inaktive</option>
                                <option value="all">Alle</option>
                            </Form.Select>
                        </th>
                        <th/>
                    </tr>
                </thead>
                <tbody>
                    {visibleUsers.map(user => (
                        <tr key={user.id}>
                            <td className="fw-semibold users-card-title">
                                {user.name}
                            </td>
                            <td data-label="E-Mail">{user.email}</td>
                            <td data-label="Weclapp-ID">{user.weclapp_id || "—"}</td>
                            <td data-label="Abteilungen" className="users-card-wide-field" aria-busy={savingId === `${user.id}:departments`} style={{minWidth: 220}}>
                                {canManage && user.can_manage ? (
                                    <ReactSelectBootstrap
                                        aria-label={`Abteilungen von ${user.name}`}
                                        isMulti
                                        menuPortalTarget={menuPortalTarget}
                                        menuPosition="fixed"
                                        isDisabled={savingId !== null}
                                        options={departments.map(department => ({value: department.id, label: department.name}))}
                                        value={(user.departments || []).map(department => ({value: department.id, label: department.name}))}
                                        onChange={value => saveInline(user, "departments", (value || []).map(option => option.value))}
                                    />
                                ) : (user.departments || []).length ? (user.departments || []).map(department => (
                                    <Badge bg="light" text="dark" className="me-1 mb-1" key={department.id}>{department.name}</Badge>
                                )) : "—"}
                            </td>
                            <td data-label="Rollen" className="users-card-wide-field" aria-busy={savingId === `${user.id}:roles`} style={{minWidth: 220}}>
                                {user.is_protected ? <SuperadminBadge/> : canManage && user.can_manage ? (
                                    <ReactSelectBootstrap
                                        aria-label={`Rollen von ${user.name}`}
                                        isMulti
                                        menuPortalTarget={menuPortalTarget}
                                        menuPosition="fixed"
                                        isDisabled={savingId !== null}
                                        options={roles.map(role => ({value: role.id, label: roleLabel(role.name)}))}
                                        value={(user.roles || []).map(role => ({value: role.id, label: roleLabel(role.name)}))}
                                        onChange={value => saveInline(user, "roles", (value || []).map(option => option.value))}
                                    />
                                ) : (user.roles || []).map(role => (
                                    <Badge pill bg={role.name === "superadmin" ? "dark" : "primary"} className="me-1 mb-1" key={role.id}>{roleLabel(role.name)}</Badge>
                                ))}
                            </td>
                            <td data-label="Status"><Badge bg={user.is_active ? "success" : "secondary"}>{user.is_active ? "Aktiv" : "Deaktiviert"}</Badge></td>
                            <td className="text-end users-card-actions">
                                {canManage && user.can_manage && <Button size="sm" variant="outline-primary" onClick={() => openEdit(user)}>Bearbeiten</Button>}
                                {user.can_impersonate && (
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={(
                                            <Tooltip id={`impersonate-hinweis-${user.id}`}>
                                                Zeigt das Ordermanagement so, wie {user.name} es sieht.
                                                Der violette Balken am unteren Rand führt jederzeit
                                                zurück. Die eigene Anmeldung von {user.name} bleibt
                                                davon unberührt.
                                            </Tooltip>
                                        )}
                                    >
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="ms-1"
                                            onClick={() => alsBenutzerAnmelden(user)}
                                        >
                                            Als Benutzer anmelden
                                        </Button>
                                    </OverlayTrigger>
                                )}
                            </td>
                        </tr>
                    ))}
                    {visibleUsers.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Keine Benutzer für diese Filter gefunden.</td></tr>}
                </tbody>
            </Table>

            <Modal show={Boolean(selected)} onHide={closeEdit} size="xl" scrollable>
                <Form
                    onSubmit={submitEdit}
                    className="d-flex flex-column overflow-hidden"
                    style={{maxHeight: "calc(100dvh - 3.5rem)"}}
                >
                    <Modal.Header closeButton className="flex-shrink-0">
                        <Modal.Title>Benutzer bearbeiten: {selected?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="overflow-auto" style={{minHeight: 0}}>
                        <UserFormFields
                            data={editForm.data}
                            setData={editForm.setData}
                            errors={editForm.errors}
                            roles={roles}
                            departments={departments}
                            printers={printers}
                            printersAvailable={printersAvailable}
                            rolesFixedBadge={selected?.is_protected ? <SuperadminBadge/> : null}
                            editable={!editForm.processing}
                            imagePreview={imagePreview}
                            onImagePreview={setImagePreview}
                        />

                        {permissionCatalog && selected && (
                            <Card className="mb-3 border-dark permission-card">
                                <Card.Header className="bg-dark text-white fw-semibold d-flex align-items-center gap-2">
                                    Rechte
                                    <HelpTooltip id="benutzer-rechte-hilfe" ariaLabel="Hilfe zu den Rechten" placement="right">
                                        Dieser Abschnitt ist nur für den Superadmin sichtbar. Rechte „über Rolle“ sind
                                        gesperrt und lassen sich nur über die Rolle ändern. Alle anderen Schalter
                                        vergeben ein Einzelrecht nur für diese Person. Einzelrechte sind rein additiv
                                        (es gibt kein Verbieten): Sie kommen zu den Rechten der Rolle hinzu.
                                    </HelpTooltip>
                                </Card.Header>
                                <Card.Body>
                                    {(
                                        <>
                                            <p className="small text-muted mb-3">
                                                Geschützte Rechte (Schloss) vergibt nur der Superadmin, einzeln an
                                                diese Person oder über eine Rolle. Über die Rolle kommende Rechte zeigt „über Rolle“.
                                                Geänderte Rollen wirken hier erst nach dem Speichern des Benutzers.
                                            </p>
                                            <PermissionMatrix
                                                key={selected.id}
                                                idPrefix={`benutzer-${selected.id}`}
                                                permissions={permissionCatalog}
                                                areas={permissionAreas}
                                                selected={permissionDraft.draft}
                                                onChange={permissionDraft.setDraft}
                                                lockedBy={selected.role_permissions || {}}
                                                readOnly={permissionsSaving}
                                            />
                                            <PermissionSaveBar
                                                count={permissionDraft.count}
                                                saving={permissionsSaving}
                                                error={permissionsError}
                                                onSave={savePermissions}
                                                onDiscard={() => {
                                                    permissionDraft.reset();
                                                    setPermissionsError("");
                                                }}
                                            />
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        <hr/>
                        <KeyValueTable
                            title="Benutzereinstellungen"
                            button={<Button type="button" variant="outline-primary" onClick={() => setShowCopySettingsModal(true)}>Einstellungen kopieren</Button>}
                            keyHeader="Schlüssel"
                            valueHeader="Wert"
                            data={selected?.settings_data || {}}
                            valueRenderer={value => <JsonView style={jsonStyle} data={value} shouldExpandNode={allExpanded}/>}
                        />
                    </Modal.Body>
                    <Modal.Footer className="flex-shrink-0">
                        <Button type="button" variant="light" disabled={editForm.processing} onClick={closeEdit}>Abbrechen</Button>
                        <Button type="submit" disabled={editForm.processing}>{editForm.processing ? "Wird gespeichert…" : "Speichern"}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ConfirmDialog
                show={confirmClose}
                title="Rechte-Änderungen verwerfen?"
                confirmLabel="Verwerfen und schließen"
                tone="danger"
                onConfirm={() => {
                    permissionDraft.reset();
                    closeEdit(true);
                }}
                onCancel={() => setConfirmClose(false)}
            >
                Für {selected?.name} gibt es ungespeicherte Änderungen an den Rechten. Beim Schließen gehen sie verloren.
            </ConfirmDialog>

            <Modal show={showCopySettingsModal} onHide={() => setShowCopySettingsModal(false)}>
                <Modal.Header closeButton><Modal.Title>Einstellungen eines Benutzers übernehmen</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ReactSelectBootstrap
                        options={copyUserOptions}
                        value={selectedUserForCopy}
                        onChange={setSelectedUserForCopy}
                        placeholder="Benutzer auswählen…"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowCopySettingsModal(false)}>Abbrechen</Button>
                    <Button disabled={!selectedUserForCopy} onClick={copySettings}>Einstellungen kopieren</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
